import type {
  ColumnState,
  GridApi,
  SortChangedEvent,
  SortModelItem
} from 'ag-grid-community'
import { isDeepEqual, isPlainObject } from 'remeda'
import type { DatasourceWithCache } from '../utils/server-side-datasource'
import {
  type GridSort,
  gridSortField,
  matchesAcceptedSorting,
  normalizeGridSorting,
  sortingColumnState
} from '../utils/sorting-state'

// Private types ---------------------------------------------------------------

type SortingLifecycle =
  | Readonly<{ status: 'ready' }>
  | Readonly<{ status: 'waiting'; request: symbol }>
  | Readonly<{ status: 'disconnected' }>
  | Readonly<{ status: 'destroyed' }>

type SortingPushResult =
  | Readonly<{ status: 'fulfilled'; value: Readonly<{ reply: unknown }> }>
  | Readonly<{ status: 'rejected' }>

// Public types ----------------------------------------------------------------

export type SortingTransport = Readonly<{
  el: HTMLElement
  pushEventTo: (
    target: string,
    event: string,
    payload: Readonly<{
      field: string
      direction: 'asc' | 'desc' | null
      multi: boolean
    }>
  ) => Promise<readonly SortingPushResult[]>
}>

export type SortingAcceptance =
  | 'unchanged'
  | 'native-reset'
  | 'refresh-required'

export type GridSorting = Readonly<{
  onSortChanged: (event: SortChangedEvent<unknown, unknown>) => void
  accept: (api: GridApi<unknown>, value: unknown) => SortingAcceptance
  canLoad: (model: readonly SortModelItem[]) => boolean
  disconnected: () => void
  reconnected: (api?: GridApi<unknown>) => void
  cleanup: () => void
}>

// Constants -------------------------------------------------------------------

// Native sortChanged is asynchronous, so keyup must not erase sort intent.
const MODIFIER_EVENTS = ['click', 'keydown', 'touchend'] as const

// Private functions -----------------------------------------------------------

/** Derive sort-only column state from the live columns and accepted server state. */
const projectSorting = (
  api: GridApi<unknown>,
  sorting: readonly GridSort[],
  liveState: ReadonlyMap<string, ColumnState>
): ColumnState[] =>
  sortingColumnState(
    (api.getColumns() ?? []).map((column) => {
      const colId = column.getColId()
      const state = liveState.get(colId)

      return {
        colId,
        sortField: gridSortField(column.getColDef().context),
        visible: state?.hide !== true,
        sort: state?.sort,
        sortIndex: state?.sortIndex
      }
    }),
    sorting
  )

// Public functions ------------------------------------------------------------

/** Route native sort intent while keeping server ordering authoritative. */
export const createGridSorting = (
  hook: SortingTransport,
  target: string,
  initialSorting: readonly GridSort[],
  requests: Pick<
    DatasourceWithCache,
    'invalidateCache' | 'retireCache' | 'resumeRequests'
  >
): GridSorting => {
  let acceptedState: readonly ColumnState[] | undefined
  let acceptedSorting = initialSorting
  let lifecycle: SortingLifecycle = { status: 'ready' }
  let multi = false

  /** Apply accepted sorts without changing unrelated column state or resetting an unchanged store. */
  const applySorting = (
    api: GridApi<unknown>,
    sorting: readonly GridSort[]
  ): SortingAcceptance => {
    if (lifecycle.status === 'destroyed' || api.isDestroyed()) {
      return 'unchanged'
    }

    const waiting = lifecycle.status === 'waiting'
    const initialized = acceptedState !== undefined
    const liveState = api.getColumnState()
    const currentState = new Map(
      liveState.map((column) => [column.colId, column])
    )
    const state = projectSorting(api, sorting, currentState)
    const changed = waiting || !isDeepEqual(sorting, acceptedSorting)
    const needsUpdate = state.some((column) => {
      const current = currentState.get(column.colId)

      return (
        (current?.sort ?? null) !== column.sort ||
        (current?.sortIndex ?? null) !== column.sortIndex
      )
    })
    acceptedSorting = sorting
    acceptedState = state
    if (waiting) {
      lifecycle = { status: 'ready' }
    }

    if (needsUpdate) {
      api.applyColumnState({ state })
    }

    // AG Grid replaces its cache for sort-index changes as well as field order.
    if (initialized && needsUpdate) {
      requests.retireCache()
    }

    if (waiting || needsUpdate) {
      return 'native-reset'
    }

    return changed ? 'refresh-required' : 'unchanged'
  }

  /** Restore server authority only while this request still owns the pending sort. */
  const recoverSort = (
    api: GridApi<unknown>,
    pending: symbol,
    sorting: readonly GridSort[] = acceptedSorting
  ): void => {
    if (
      lifecycle.status !== 'waiting' ||
      lifecycle.request !== pending ||
      api.isDestroyed()
    ) {
      return
    }

    requests.invalidateCache()
    applySorting(api, sorting)
    requests.resumeRequests()
  }

  /** Reconcile explicit server corrections or failed target deliveries. */
  const settleSort = (
    api: GridApi<unknown>,
    pending: symbol,
    results: readonly SortingPushResult[]
  ): void => {
    if (
      results.length === 0 ||
      results.some((result) => result.status === 'rejected')
    ) {
      recoverSort(api, pending)
      return
    }

    for (const result of results) {
      if (result.status === 'fulfilled' && isPlainObject(result.value.reply)) {
        const sorting = normalizeGridSorting(result.value.reply.sorting)

        if (sorting) {
          recoverSort(api, pending, sorting)
        }
      }
    }
  }

  /** Observe push failures instead of relying on Phoenix's success-only callback. */
  const sendSort = (
    api: GridApi<unknown>,
    pending: symbol,
    payload: Parameters<SortingTransport['pushEventTo']>[2]
  ): void => {
    try {
      hook.pushEventTo(target, 'grid_sort', payload).then(
        (results) => settleSort(api, pending, results),
        () => recoverSort(api, pending)
      )
    } catch {
      recoverSort(api, pending)
    }
  }

  /** Capture intent before native sorting; touch taps never request multi-sort. */
  const captureModifiers = (event: Event): void => {
    if (event.type === 'touchend') {
      multi = false
    }

    if (event instanceof MouseEvent || event instanceof KeyboardEvent) {
      multi = event.shiftKey
    }
  }

  for (const type of MODIFIER_EVENTS) {
    hook.el.addEventListener(type, captureModifiers, true)
  }

  return {
    onSortChanged: (event): void => {
      if (
        lifecycle.status === 'destroyed' ||
        event.source !== 'uiColumnSorted'
      ) {
        return
      }

      if (lifecycle.status === 'disconnected') {
        requests.invalidateCache()
        applySorting(event.api, acceptedSorting)
        return
      }

      // v36 emits cleared columns first and the initiating column last.
      const column = event.columns?.[event.columns.length - 1]
      const field = column && gridSortField(column.getColDef().context)

      if (!(column && field)) {
        return
      }

      const pending = Symbol('grid-sort')
      lifecycle = { status: 'waiting', request: pending }
      // Native sorting has already replaced the cache before this UI event.
      requests.retireCache()
      sendSort(event.api, pending, {
        field,
        direction: column.getSortDef()?.direction ?? null,
        multi
      })
    },
    accept: (api, value): SortingAcceptance => {
      const sorting =
        value === undefined ? acceptedSorting : normalizeGridSorting(value)

      return sorting ? applySorting(api, sorting) : 'unchanged'
    },
    canLoad: (model): boolean =>
      lifecycle.status === 'ready' &&
      acceptedState !== undefined &&
      matchesAcceptedSorting(model, acceptedState),
    disconnected: (): void => {
      if (lifecycle.status !== 'destroyed') {
        lifecycle = { status: 'disconnected' }
        requests.invalidateCache()
      }
    },
    reconnected: (api): void => {
      if (lifecycle.status !== 'disconnected') {
        return
      }

      lifecycle = { status: 'ready' }
      if (api && !api.isDestroyed()) {
        applySorting(api, acceptedSorting)
        requests.resumeRequests()
      }
    },
    cleanup: (): void => {
      lifecycle = { status: 'destroyed' }
      for (const type of MODIFIER_EVENTS) {
        hook.el.removeEventListener(type, captureModifiers, true)
      }
    }
  }
}
